<template>
    <div v-if="field">
        <div class="form-actions">
            <b-button variant="primary" @click="editor.show = true">{{ $t('edit') }}</b-button>
        </div>

        <main-dialog title="Edit constraints" v-model="editor.show" @ok="save" @hide="hideConstraints" ok-only>
            <b-form-group
                v-if="canDisplayRequired"
                  :label="this.$t('required')"
                  label-for="required"
                  :label-cols="3"
                  :horizontal="false">
            <c-switch class="mx-1" color="success" variant="pill" v-model="constraints.required"/>
            </b-form-group>

            <b-form-group
                v-if="canDisplayPlaceholder"
                  :label="this.$t('placeholder')"
                  label-for="required"
                  :label-cols="3"
                  :horizontal="false">
            <b-form-input type="text" v-model="constraints.placeholder"></b-form-input>
            </b-form-group>

            <b-form-group
                v-if="defaultInputConfig"
                  :label="this.$t('default')"
                  label-for="default_value"
                  :label-cols="3"
                  :horizontal="false">
                <b-form-input
                    v-if="defaultInputConfig.type === 'number'"
                    type="number"
                    v-model="constraints.default">
                </b-form-input>
                <b-form-input
                    v-else-if="defaultInputConfig.type === 'date'"
                    type="date"
                    v-model="constraints.default">
                </b-form-input>
                <b-form-input
                    v-else-if="defaultInputConfig.type === 'datetime-local'"
                    type="datetime-local"
                    v-model="constraints.default">
                </b-form-input>
                <b-form-input
                    v-else-if="defaultInputConfig.type === 'time'"
                    type="time"
                    v-model="constraints.default">
                </b-form-input>
                <b-form-input
                    v-else-if="defaultInputConfig.type === 'email'"
                    type="email"
                    v-model="constraints.default">
                </b-form-input>
                <div v-else-if="defaultInputConfig.type === 'boolean'" class="d-flex align-items-center">
                    <c-switch
                        class="mx-1"
                        color="success"
                        variant="pill"
                        :checked="constraints.default === 'true'"
                        @change="onDefaultBoolChange">
                    </c-switch>
                    <span class="ml-2 text-muted">{{ constraints.default === 'true' ? 'true' : 'false' }}</span>
                </div>
                <VSelect
                    v-else-if="defaultInputConfig.type === 'select'"
                    v-model="constraints.default"
                    :options="defaultInputConfig.options"
                    :taggable="defaultInputConfig.taggable">
                </VSelect>
                <b-form-input
                    v-else
                    type="text"
                    v-model="constraints.default">
                </b-form-input>
                <small v-if="hasIdl" class="form-text text-muted mt-1">{{ $t('defaultValueIdlHint') }}</small>
            </b-form-group>

            <div v-if="field.module == 'input' && field.type == 'number'">
                <InputField :labelCols="3" name="min_length" type="number" :label="this.$t('minlength')" v-model.number="constraints.length.min" :rules="{}" @error="fieldError"/>

                <InputField :labelCols="3" name="max_length" type="number" :label="this.$t('maxLength')" v-model.number="constraints.length.max" :rules="{}" @error="fieldError"/>
            </div>
            <!-- DISPLAY THIS IF MODULE IS INPUT AND TYPE STRING -->
            <div v-if="(field.module == 'input' && (field.type == 'text' || field.type == 'password'))">
                <InputField :labelCols="3" name="min_length" type="number" :label="this.$t('minlength')" v-model.number="constraints.length.min" :rules="{required: true, length: {min:0, max: 99999}}" @error="fieldError"/>

                <InputField :labelCols="3" name="max_length" type="number" :label="this.$t('maxLength')" v-model.number="constraints.length.max" :rules="{required: true, length: {min:0, max: 99999}}" @error="fieldError"/>

                <b-form-group
                    :description="this.$t('notAllowedChars')"
                    :label="this.$t('notAllowedStrings')"
                    label-for="not_allowed_words"
                    :label-cols="3"
                    :horizontal="false">
                    <b-form-input type="text" v-model="constraints.not_allowed_words"></b-form-input>
                </b-form-group>

                <b-form-group
                    :description="this.$t('validationRegex')"
                    :label="this.$t('regex')"
                    label-for="regex"
                    :label-cols="3"
                    :horizontal="false">
                    <b-form-input type="text" v-model="constraints.regex"></b-form-input>
                </b-form-group>
            </div>
            <!-- DISPLAY THIS IF MODULE IS INPUT AND TYPE STRING -->

            <div v-if="field.module == 'input'">
                <ImageConstraints v-model="constraints" v-if="field.type == 'image'"/>
                <FileConstraints v-model="constraints" v-if="field.type == 'file'"/>
                <Texti18nConstraints v-model="constraints" v-if="field.type == 'text_i18n'"/>
            </div>

            <!-- DISPLAY THIS IF MODULE IS INPUT AND TYPE STRING -->
            <div v-if="(field.module == 'text')">
                <InputField :labelCols="3" name="min_length" type="number" :label="this.$t('minLength')" v-model.number="constraints.length.min" :rules="{required: false}" @error="fieldError"/>

                <InputField :labelCols="3" name="max_length" type="number" :label="this.$t('maxLength')" v-model.number="constraints.length.max" :rules="{required: false}" @error="fieldError"/>

                <b-form-group
                    :description="this.$t('notAllowedChars')"
                    :label="this.$t('notAllowedWords')"
                    label-for="not_allowed_words"
                    :label-cols="3"
                    :horizontal="false">
                    <b-form-input type="text" v-model="constraints.not_allowed_words"></b-form-input>
                </b-form-group>

                <b-form-group
                    :description="this.$t('validationRegex')"
                    :label="this.$t('regex')"
                    label-for="regex"
                    :label-cols="3"
                    :horizontal="false">
                    <b-form-input type="text" v-model="constraints.regex"></b-form-input>
                </b-form-group>
            </div>
            <!-- DISPLAY THIS IF MODULE IS INPUT AND TYPE STRING -->

            <div v-if="field.module == 'picker'">
                <DateConstraints v-model="constraints" v-if="field.type == 'date'"/>
                <PickerTimeConstraints v-model="constraints" v-if="field.type == 'time'" @error="fieldError"/>
                <DateTimeConstraints v-model="constraints" v-if="field.type == 'datetime'" @error="fieldError"/>
            </div>

            <div v-if="field.module == 'select'">
                <SelectTextConstraints v-model="constraints" v-if="field.type == 'text'"/>
                <SelectNumberConstraints v-model="constraints" v-if="field.type == 'number'"/>
                <SelectRelationConstraints v-model="constraints" :field="field" v-if="field.type == 'relation'"/>
                <SelectExternalConstraints v-model="constraints" :field="field" v-if="field.type == 'external'"/>
            </div>

            <div v-if="field.module == 'checkbox'">
                <CheckboxTextConstraints v-model="constraints" v-if="field.type == 'text'"/>
                <CheckboxNumberConstraints v-model="constraints" v-if="field.type == 'number'"/>
            </div>

            <div v-if="field.module == 'radio'">
                <RadioTextConstraints v-model="constraints" v-if="field.type == 'text'"/>
                <RadioNumberConstraints v-model="constraints" v-if="field.type == 'number'"/>
            </div>

            <div v-if="field.module == 'table' && field.type == 'custom'">
                <TableCustomConstraints v-model="constraints"/>
                <TableCustomFields v-model="constraints"/>
            </div>

            <div v-if="field.module == 'image' && field.type == 'carousel'">
                <ImageCarousel v-model="constraints" />
            </div>

            <div v-if="field.module == 'fieldset'">
                <DefaultFieldsetConstraints v-model="constraints" v-if="field.type == 'default'" :component="component"/>
            </div>

            <div v-if="field.module == 'data'">
                <DataJsonConstraints v-model="constraints" v-if="field.type == 'json'"/>
            </div>
        </main-dialog>
    </div>
</template>

<script>
import { Switch as cSwitch } from '@coreui/vue'
import Input from '@/components/form/Input'
import CodeEditor from '@/components/form/Editor/Code'
import VSelect from 'vue-select'

export default {
    components: {
        cSwitch,
        InputField: Input,
        CodeEditor,
        VSelect,
        DateConstraints: () => import('../../identityFields/constraints/picker/Date'),
        SelectTextConstraints: () => import('../../identityFields/constraints/select/Text'),
        SelectNumberConstraints: () => import('../../identityFields/constraints/select/Number'),
        SelectExternalConstraints:() => import('../../identityFields/constraints/select/External'),
        CheckboxTextConstraints:() => import('../../identityFields/constraints/checkbox/Number'),
        RadioTextConstraints:() => import('../../identityFields/constraints/radio/Text'),
        RadioNumberConstraints:() => import('../../identityFields/constraints/radio/Number'),
        SelectRelationConstraints:() => import('../../identityFields/constraints/select/Relation'),
        TableCustomConstraints: () => import('../../identityFields/constraints/table/Custom'),
        TableCustomFields: () => import('../../identityFields/constraints/table/Fields'),
        PickerTimeConstraints: () => import('../../identityFields/constraints/picker/Time'),
        DateTimeConstraints: () => import('../../identityFields/constraints/picker/DateTime'),
        ImageCarousel: () => import("../../identityFields/constraints/image/Carousel"),
        ImageConstraints: () => import("../../identityFields/constraints/input/Image"),
        FileConstraints: () => import("../../identityFields/constraints/input/File"),
        DefaultFieldsetConstraints: () => import("../../identityFields/constraints/fieldset/Default"),
        DataJsonConstraints: () => import("../../identityFields/constraints/data/Json"),
        Texti18nConstraints: () => import("../../identityFields/constraints/input/Texti18n")
    },
    props: {
        field: {
            type: Object
        },
        value: {
            type: Object
        },
        component: {
            type: Object
        }
    },
    watch: {
        "constraints.length.min": function(nval, oval) {
            if (nval == "" || nval === null || nval === undefined) {
                this.constraints.length.min = 0;
            }

            if (nval > this.constraints.length.max) {
                this.constraints.length.max = nval
            }
        },

        "constraints.length.max": function(nval, oval) {
            if (nval == "" || nval === null || nval === undefined) {
                this.constraints.length.max = 0;
            }

            if (nval < this.constraints.length.min) {
                this.constraints.length.min = nval
            }
        }
    },
    data: function() {
        return {
            errors: [],
            editor: {
                show: false
            }
        }
    },
    computed: {
        canDisplayRequired() {
            return (this.field.module != "table")
        },

        canDisplayPlaceholder() {
            return (
                this.field.module != "table" &&
                this.field.module != "checkbox" &&
                this.field.module != "radio" &&
                this.field.type != "relation"
                )
        },

defaultInputConfig() {
            if (!this.field) return null
            var module = this.field.module
            var type = this.field.type
            var key = module + ':' + type

            var hideList = [
                'data:json',
                'fieldset:default',
                'input:image',
                'input:file',
                'input:text_i18n',
                'image:carousel',
                'picker:daterange',
                'select:relation',
                'select:external',
                'table:default',
                'table:custom',
                'checkbox:text',
                'checkbox:number',
                'radio:text',
                'radio:number'
            ]

            if (hideList.indexOf(key) !== -1) return null

            if (module === 'input') {
                if (type === 'number') return { type: 'number' }
                if (type === 'date') return { type: 'date' }
                if (type === 'email') return { type: 'email' }
                return { type: 'text' }
            }

            if (module === 'picker') {
                if (type === 'date') return { type: 'date' }
                if (type === 'datetime') return { type: 'datetime-local' }
                if (type === 'time') return { type: 'time' }
                return null
            }

            if (module === 'select') {
                var items = this.constraints && this.constraints.selectables && this.constraints.selectables.items
                var isCustom = this.constraints && this.constraints.selectables && this.constraints.selectables.custom
                return {
                    type: 'select',
                    options: items || [],
                    taggable: !!isCustom || !(items && items.length)
                }
            }

            if (module === 'checkbox' && type === 'bool') {
                return { type: 'boolean' }
            }

            return { type: 'text' }
        },

        hasIdl() {
            if (!this.component || !this.component.settings || !this.component.settings.idl) return false
            return Object.keys(this.component.settings.idl).length > 0
        },

        constraints: {
            get() {
                return this.value;
            },

            set(val) {
                console.log("field constraints set", val)
                this.$emit('input', val);
            }
        }
    },
    methods: {
        save(modal) {
            if (this.checkForErrors()) {
                modal.preventDefault()
                this.$mainDialog.msgBoxOk("Some fields are not valid!")

                return
            }

            this.$emit('input', this.constraints)
        },

        hideConstraints(modal) {
            if (modal.trigger != "ok")
                this.save(modal)
        },

        fieldError(error) {
            let found = this.errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors() {
            return this.errors.find((i, e) => {
                return i.error
            })
        },

        onDefaultBoolChange(val) {
            this.constraints.default = val ? 'true' : 'false'
        },
    }
}
</script>
